import { palettes, type PaletteShape } from "./palette";
import { APP_THEME_SPECS, DEFAULT_APP_THEME_ID, getThemeSpec, type AppThemeId } from "./themeSpecs";

export type ColorMode = "light" | "dark";

const VAR_PREFIX = "--fx";

const toVarName = (path: string[]) => `${VAR_PREFIX}-${path.join("-").toLowerCase()}`;

const flatten = (value: unknown, path: string[], sink: Record<string, string>) => {
  if (value && typeof value === "object") {
    Object.entries(value as Record<string, unknown>).forEach(([key, child]) => flatten(child, [...path, key], sink));
    return sink;
  }
  sink[toVarName(path)] = String(value);
  return sink;
};

const mirrorWithVarRefs = <T,>(value: T, path: string[]): T => {
  if (value && typeof value === "object") {
    const mirrored: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, child]) => {
      mirrored[key] = mirrorWithVarRefs(child, [...path, key]);
    });
    return mirrored as T;
  }
  return `var(${toVarName(path)}, ${String(value)})` as unknown as T;
};

export const themeVars = APP_THEME_SPECS.reduce(
  (accumulator, spec) => {
    accumulator[spec.id] = flatten(palettes[spec.id], [], {});
    return accumulator;
  },
  {} as Record<AppThemeId, Record<string, string>>,
);

export const varsForTheme = (id: AppThemeId) => themeVars[id] ?? themeVars[DEFAULT_APP_THEME_ID];

export const paletteForTheme = (id?: string | null): PaletteShape => palettes[getThemeSpec(id).id];

export const lightVars = themeVars.flownect;
export const darkVars = themeVars.midnight;

export const tokenRefs = mirrorWithVarRefs(palettes[DEFAULT_APP_THEME_ID], []);

export const surface = tokenRefs.surface;

export const isCssVarColor = (value: unknown): value is string =>
  typeof value === "string" && (value.includes("var(") || value.includes("color-mix("));

export const alphaVar = (color: string, opacity: number) => {
  if (!isCssVarColor(color)) return null;
  const percentage = Math.round(Math.max(0, Math.min(1, opacity)) * 10000) / 100;
  return `color-mix(in srgb, ${color} ${percentage}%, transparent)`;
};

export const resolveCssColor = (color: string, element?: Element | null) => {
  if (!isCssVarColor(color) || typeof window === "undefined") return color;
  const probe = document.createElement("span");
  probe.style.color = color;
  probe.style.position = "absolute";
  probe.style.opacity = "0";
  probe.style.pointerEvents = "none";
  (element ?? document.body).appendChild(probe);
  const resolved = window.getComputedStyle(probe).color;
  probe.remove();
  return resolved || color;
};
